import React, { useState } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import Table from "./Table";
import {
  MITARBEITER_ARCHIVE_DETAILS,
  MITARBEITER_DETAILS,
} from "../../utils/TableConstanten";
import * as FaIcons from "react-icons/fa";
import { CiCircleCheck } from "react-icons/ci";
import TableActionButtons from "./TableActionButtons";
import logger from "../../logging/logger";

const TableBody = ({
  data,
  columns,
  stickyActions,
  onDelete,
  onArchive,
  onRestore,
  parentId,
  onEdit,
  rowClicks,
  onNachuntersuchung,
  embedded,
  filters,
  is_archived,
  onExport,
  onHistory,
  apiEndpoint,
  onEmailLogDetail,
}) => {
  const [expandedRowId, setExpandedRowId] = useState(null);

  const formatValue = (value, format) => {
    if (format && value) {
      return moment(value).format(format);
    }
    return value;
  };

  const getNestedValue = (obj, path) => {
    const parts = path.split(".");
    let current = obj;

    for (let part of parts) {
      if (Array.isArray(current)) {
        current = current
          .map((item) => item[part])
          .filter(Boolean)
          .join(", ");
      } else {
        current = current[part];
      }
      if (!current) break;
    }
    return current;
  };

  const showIcon = (value, path) => {
    const IconComponent = FaIcons[path.icon] || FaIcons.FaRegFileAlt; // Fallback-Icon

    return (
      <div className="dateiIconWrapper">
        {value[path.dbName]}
        <IconComponent
          style={{
            color: "#f47a37",
            fontSize: "1rem",
            fontWeight: "400 !important",
          }}
        />
      </div>
    );
  };

  const stackGroup = (column, row) => {
    if (column.group) {
      return column.group.map((field) => {
        if (!row[field] || row[field] === 0) {
          return "";
        }
        return (
          <div key={field} className="statistic">
            <span className={`statisticCircle ${field}`}></span>
            {row[field]}
          </div>
        );
      });
    }
    return "";
  };

  const handleRowClick = (
    _e,
    rowId,
    is_archived = false,
    firmenkarteiId = 0,
  ) => {
    if (!is_archived) {
      setExpandedRowId((prevId) => (prevId === rowId ? null : rowId));
    } else {
      logger.log("showHistory", parentId, firmenkarteiId);
      onHistory(parentId, firmenkarteiId);
    }
  };

  const span = columns.length + 1;
  let html = [
    <tr key="no-data">
      <td colSpan={span} style={{ fontWeight: "bold" }}>
        Keine Daten Vorhanden
      </td>
    </tr>,
  ];

  if (data.length > 0) {
    html = data.map((row, rowIndex) => {
      let classNames = row.status + "-border ";
      classNames +=
        rowClicks || (apiEndpoint === "emailLog" && onEmailLogDetail)
          ? "mouseClick"
          : "";
      return (
        <React.Fragment key={rowIndex}>
          <tr
            className={classNames}
            onClick={
              rowClicks || (apiEndpoint === "emailLog" && onEmailLogDetail)
                ? (e) => {
                    if (apiEndpoint === "emailLog" && onEmailLogDetail) {
                      onEmailLogDetail(row);
                      return;
                    }
                    handleRowClick(
                      e,
                      row.id,
                      is_archived && embedded,
                      row["firmenkartei.id"],
                    );
                  }
                : undefined
            }
          >
            {columns.map((column) => (
              <td key={column.dbName}>
                {column.dbName === "statistik" && column.group
                  ? stackGroup(column, row)
                  : column.dbName === "dateiAnzahl"
                    ? showIcon(row, column)
                    : column.render
                      ? column.render(getNestedValue(row, column.dbName), row)
                      : formatValue(
                          getNestedValue(row, column.dbName),
                          column.format,
                        )}
              </td>
            ))}
            <td
              className={stickyActions ? "sticky-actions" : undefined}
              style={{ textAlign: "right" }}
            >
              <TableActionButtons
                row={row}
                rowClicks={rowClicks}
                embedded={embedded}
                is_archived={is_archived}
                onNachuntersuchung={onNachuntersuchung}
                onHistory={onHistory}
                parentId={parentId}
                apiEndpoint={apiEndpoint}
                onDelete={onDelete}
                onArchive={onArchive}
                onEdit={onEdit}
                onExport={onExport}
                expandedRowId={expandedRowId}
                handleRowClick={handleRowClick}
              />
            </td>
          </tr>
          {!embedded && expandedRowId === row.id && (
            <tr className="expanded-row">
              <td colSpan={columns.length + 1}>
                <Table
                  apiEndpoint={
                    is_archived
                      ? MITARBEITER_ARCHIVE_DETAILS + "/" + row.id
                      : MITARBEITER_DETAILS + "/" + row.id
                  }
                  embedded={true}
                  rowClicks={is_archived ?? false}
                  onRowEdit={onEdit}
                  onNachuntersuchung={onNachuntersuchung}
                  filters={filters}
                  report={!is_archived}
                  archive={is_archived}
                  onRestore={() => onRestore(row.id)}
                  parentId={row.id}
                />
              </td>
            </tr>
          )}
        </React.Fragment>
      );
    });
  }

  // Button auch anzeigen wenn keine Daten vorhanden sind (für archivierte Mitarbeiter ohne Untersuchungen)
  if (is_archived && embedded && onRestore) {
    html.push(
      <tr key="restore-cta" className="restore-cta">
        <td colSpan={columns.length + 1} style={{ textAlign: "end" }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRestore(parentId);
            }}
            className="btn btn-secondary tableActionButtonArchive"
          >
            <CiCircleCheck /> Mitarbeiter aktivieren
          </button>
        </td>
      </tr>,
    );
  }
  return <tbody className="table-group-divider">{html}</tbody>;
};

TableBody.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      dbName: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      format: PropTypes.string,
    }),
  ).isRequired,
  onDelete: PropTypes.any,
  onEdit: PropTypes.any,
  stickyActions: PropTypes.bool,
  onArchive: PropTypes.any,
  rowClicks: PropTypes.any,
  embedded: PropTypes.any,
  onNachuntersuchung: PropTypes.any,
};

export default TableBody;
